import { createRef, useEffect, useContext } from "react";
import EventRulePriceForm from "../components/EventRulePriceForm";
import EventRulePriceTable from "../components/EventRulePriceTable";
import getConfig from "../configs/tableConfig";
import JSONSchemaForm from "@rjsf/core";

import getFiltersConfig from "../configs/filterConfig";
import { Card } from "react-bootstrap";
import getFilterUIConfig from "../configs/filterUIConfig";
import moment from "moment";
import { EventRulePriceContext } from "../contexts/EventRulePriceContext";
import useIndex from "../services/useIndex";
import useGet from "../services/useGet";
import useInitialize from "../services/useInitialize";
import useStore from "../services/useStore";
import useUpdate from "../services/useUpdate";
import usePrepareRemove from "../services/usePrepareRemove";
import useRemove from "../services/useRemove";
import CrudModuleFactoryModal from "@beeldit/core/components/CrudModuleFactoryModal";
import useFilters from "@beeldit/core/helpers/useFilters";
import AddQueryParamsToURL from "@beeldit/core/helpers/AddQueryParamsToURL";
import BeelditTableFilters from "@beeldit/core/components/BeelditTableFilters";
import CrudModuleFactoryConfirmationModal from "@beeldit/core/components/CrudModuleFactoryConfirmationModal";
import BeelditTablePagination from "@beeldit/core/components/BeelditTablePagination";
import BeelditTableHeaderActions from "@beeldit/core/components/BeelditTableHeaderActions";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { t } from "i18next";

function EventRulePricePage() {
  const { user } = useContext(AuthContext);

  const addQueryParamsToURL: any = AddQueryParamsToURL();

  /** Crud functions */
  const index = useIndex();
  const indexFunction = () => index();

  const get = useGet();
  const getFunction = (id: any) => get(id);

  const initialize = useInitialize();
  const initializeFunction = () => initialize();

  /**
   * Form store success function definition
   */
  const onSuccessStore = () => {
    handleClose(); // We pass the function to close the modal on success
    index(); // We pass the function to refresh the table on success
  };

  const store = useStore();
  const storeFunction = () => store(onSuccessStore);

  /**
   * Form update success function definition
   */
  const onSuccessUpdate = () => {
    handleClose(); // We pass the function to close the modal on success
    index(); // We pass the function to refresh the table on success
  };

  const update = useUpdate();
  const updateFunction = () => update(onSuccessUpdate);

  const prepareRemove = usePrepareRemove();
  const prepareRemoveFunction = (id: any) => prepareRemove(id);

  const remove = useRemove();
  const onSuccessRemove = () => {
    indexFunction();
  };
  const removeFunction = () => remove(onSuccessRemove);

  const copy = (element: any) => {
    element.id = null;
    element.date = moment().format("YYYY-MM-DD");
    element.minutes = 0;
    return element;
  };

  const copyFunction = (id: any) => get(id, copy);

  /** Filter loading */
  const { filtersLoaded } = useContext(EventRulePriceContext);
  const { tableFilters, setTableFilters } = useContext(EventRulePriceContext);
  useFilters(
    { customer_id: null, status_id: null, report_id: null },
    EventRulePriceContext
  );

  /**Order by config */
  const { orderBy, setOrderBy } = useContext(EventRulePriceContext);

  const onOrderByChange = (orderBy: string) => {
    setOrderBy(orderBy);
  };

  /**
   * Pagination config
   */
  const { paginationConfig, setPaginationConfig } = useContext(
    EventRulePriceContext
  );

  const onPaginationChange = (page: number) => {
    setPaginationConfig({ ...paginationConfig, page: page });
  };

  /** Index table funcion definition */
  const { tableData } = useContext(EventRulePriceContext);

  useEffect(() => {
    if (filtersLoaded) {
      index();
    }
  }, [tableFilters, paginationConfig, filtersLoaded, orderBy]);

  /** Creation and edition modal function and state definition */
  const { showModal, setShowModal } = useContext(EventRulePriceContext);
  const handleClose = () => setShowModal(false); // We pass the function to close the modal on success

  /** Creation and edition modal element definition */
  const { element, setElement } = useContext(EventRulePriceContext);

  /**
   * Form Reference definition
   */
  const formRef = createRef<JSONSchemaForm>();

  /**
   * Creation and edition modal configuration definition
   */
  const modalConfig = {
    title:
      element && element.id
        ? "Edit Event Rule Price"
        : "Create Event Rule Price ",
    context: EventRulePriceContext,
    onSubmit: () => formRef.current?.formElement.current.requestSubmit(), //We pass the function for the submit of the modal
  };

  /**
   * Delete Confirmation modal configuration definition
   */
  const deleteConfirmationModalConfig = {
    context: EventRulePriceContext,
    onSubmit: () => removeFunction(), //We pass the function for the submit of the modal
  };

  /**
   * Form configuration definition
   */
  const formConfig = {
    formRef: formRef, // We pass the ref to the form
    onSubmit: (form: any) => {
      if (form.formData.id) {
        // If the element has an id, we update it
        updateFunction();
      } else {
        // else we create it
        storeFunction();
      }
    },
    element: element, // We pass the element to the form in order to render it in the modal form
  };

  /**
   * Table configuration definition
   */
  const tableConfig = getConfig({
    initializeFunction, // We pass the function to initialize a new element (used in the create button)
    indexFunction, // We pass the function to index the table
    getFunction, // We pass the function to get an element (used in the edit button)
    prepareRemoveFunction,
    copyFunction,
  });

  /** Table Filters configuration */
  const filterUIConfig = getFilterUIConfig(user);
  const filterConfig = {
    schema: getFiltersConfig(user),
    uiSchema: filterUIConfig.uiScheme,
    widgets: filterUIConfig.widgets,
    element: tableFilters,
    onChange: (form: any) => {
      setTableFilters(form.formData);
      addQueryParamsToURL(form.formData);
    },
  };

  return (
    <Card>
      <Card.Header className="d-flex align-items-center justify-content-between ps-3 text-bg-primary">
        <span>{t("eventruleprice")}</span>
        <div>
          <BeelditTableHeaderActions
            actions={tableConfig.actions}
          ></BeelditTableHeaderActions>
        </div>
      </Card.Header>

      <Card.Body>
        {filtersLoaded ? (
          <>
            <BeelditTableFilters formConfig={filterConfig} />
            <EventRulePriceTable
              tableData={tableData}
              tableConfig={tableConfig}
              onOrderByChange={onOrderByChange}
            />
            <BeelditTablePagination
              paginationConfig={paginationConfig}
              totalElements={tableData.totalElements}
              onPaginationChange={onPaginationChange}
            ></BeelditTablePagination>
          </>
        ) : null}
        <CrudModuleFactoryModal modalConfig={modalConfig}>
          <EventRulePriceForm formConfig={formConfig} />
        </CrudModuleFactoryModal>
        <CrudModuleFactoryConfirmationModal
          modalConfig={deleteConfirmationModalConfig}
        >
          ¿Desear eliminar el elemento?
        </CrudModuleFactoryConfirmationModal>
      </Card.Body>
    </Card>
  );
}

export default EventRulePricePage;
